import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { customScopeEditorHtml } from "./scope";
export type CustomScopeEditorHandle = { insert: (token: string) => void; focus: () => void };
const modules = { toolbar: ["bold", "italic", { list: "bullet" }] };
const formats = ["bold", "italic", "list"];
const CustomScopeEditor = forwardRef<
  CustomScopeEditorHandle,
  {
    value: string;
    format?: string | null;
    disabled: boolean;
    onChange: (html: string) => void;
  }
>(({ value, format, disabled, onChange }, ref) => {
  const editor = useRef<ReactQuill>(null);
  const selection = useRef<{ index: number; length: number } | null>(null);
  const html = useMemo(() => customScopeEditorHtml(value, format), [value, format]);
  useEffect(() => {
    const root = editor.current?.getEditor().root;
    root?.setAttribute("aria-label", "Scope of work");
    root?.setAttribute("role", "textbox");
    root?.setAttribute("aria-multiline", "true");
    const toolbar = root?.closest(".quill")?.querySelector(".ql-toolbar");
    for (const [selector, label] of [
      [".ql-bold", "Bold"],
      [".ql-italic", "Italic"],
      [".ql-list", "Bullet list"],
    ]) {
      const button = toolbar?.querySelector(selector);
      button?.setAttribute("aria-label", label);
      button?.setAttribute("title", label);
    }
  }, []);
  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor.current?.focus(),
      insert: (token) => {
        if (disabled || !editor.current) return;
        const quill = editor.current.getEditor();
        const range = selection.current ?? { index: Math.max(0, quill.getLength() - 1), length: 0 };
        quill.deleteText(range.index, range.length, "user");
        quill.insertText(range.index, token, "user");
        quill.setSelection(range.index + token.length, 0, "silent");
        selection.current = { index: range.index + token.length, length: 0 };
      },
    }),
    [disabled]
  );
  return (
    <Box
      sx={{
        "& .ql-toolbar": {
          py: 0.25,
          px: 0.5,
          borderRadius: "4px 4px 0 0",
          ...(disabled ? { display: "none" } : {}),
        },
        "& .ql-container": { fontFamily: "inherit", fontSize: 13, borderRadius: "0 0 4px 4px" },
        "& .ql-editor": { minHeight: 48, padding: "6px 8px", overflowWrap: "anywhere" },
      }}
    >
      <ReactQuill
        ref={editor}
        theme="snow"
        value={html}
        modules={modules}
        formats={formats}
        readOnly={disabled}
        onChange={(content, _delta, source) => {
          if (source === "user") onChange(content);
        }}
        onChangeSelection={(range) => {
          if (range) selection.current = range;
        }}
        onFocus={() => {
          editor.current?.getEditor().root.setAttribute("aria-label", "Scope of work");
        }}
      />
    </Box>
  );
});
export default CustomScopeEditor;
